import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import {
  AdminTableBody,
  AdminMetadataText,
  AdminSupportingText,
  AdminHeaderAction,
  AdminDetailPaginationFooter,
} from "../../../sections/AdminDashboard/shared/adminUx";
import { BUSINESS_INBOX_CONTENT_CLASS, BUSINESS_TABLE_CLASS } from "../../../sections/BusinessDashboard/shared/businessLayoutSpec";

// Mock: current business user id (in real app from auth)
const CURRENT_BUSINESS_ID = "B-8814";
const DEFAULT_AVATAR = require("../../../assets/img/User.png");

const STATUS_FROM_PATH = { all: null, open: "Open", new: "New", resolved: "Resolved", closed: "Closed" };

// Random users for ticket list (same style as messages inbox)
const TICKET_USERS = [
  { name: "Sarah Mitchell", image: "https://i.pravatar.cc/150?img=12" },
  { name: "Michael Chen", image: "https://i.pravatar.cc/150?img=33" },
  { name: "Emma Thompson", image: "https://i.pravatar.cc/150?img=47" },
  { name: "David Rodriguez", image: "https://i.pravatar.cc/150?img=52" },
  { name: "Lisa Anderson", image: "https://i.pravatar.cc/150?img=68" },
  { name: "James Wilson", image: "https://i.pravatar.cc/150?img=15" },
  { name: "Rachel Green", image: "https://i.pravatar.cc/150?img=28" },
  { name: "Robert Taylor", image: "https://i.pravatar.cc/150?img=45" },
  { name: "Jennifer White", image: "https://i.pravatar.cc/150?img=25" },
];

const AllTickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegment = location.pathname.split("/").filter(Boolean).pop() || "all";
  const statusFilter = STATUS_FROM_PATH[pathSegment] !== undefined ? STATUS_FROM_PATH[pathSegment] : null;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const perPage = 10;

  const generateMyTickets = () => {
    const now = new Date();
    const raw = [
      {
        ticketId: "TCK-19291",
        subject: "Payment declined",
        priority: "High",
        category: "Billing",
        status: "Open",
        lastReply: "Please check that your card has sufficient funds and try again.",
        createdDate: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19295",
        subject: "Feature request",
        priority: "Low",
        category: "General Inquiry",
        status: "New",
        lastReply: null,
        createdDate: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19279",
        subject: "Account locked",
        priority: "High",
        category: "Account Access",
        status: "Open",
        lastReply: "We've unlocked your account. Please reset your password to sign in.",
        createdDate: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19292",
        subject: "Subscription cancellation",
        priority: "High",
        category: "Billing",
        status: "New",
        lastReply: null,
        createdDate: new Date(now.getTime() - 17 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 17 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19287",
        subject: "Account upgrade request",
        priority: "Medium",
        category: "General Inquiry",
        status: "Open",
        lastReply: "You can upgrade from Settings → Subscription. Need help choosing a plan?",
        createdDate: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19293",
        subject: "Invoice download problem",
        priority: "High",
        category: "Billing",
        status: "Open",
        lastReply: "Try using a different browser or clear cache. If it persists we'll email the invoice.",
        createdDate: new Date(now.getTime() - 23 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19288",
        subject: "Password reset completed",
        priority: "Low",
        category: "Account Access",
        status: "Resolved",
        lastReply: "Glad we could help. Your account is secure again.",
        createdDate: new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19289",
        subject: "API integration question",
        priority: "Medium",
        category: "General Inquiry",
        status: "Closed",
        lastReply: "Documentation is at developers.tradesposter.com. Ticket closed.",
        createdDate: new Date(now.getTime() - 26 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
      {
        ticketId: "TCK-19294",
        subject: "Bulk upload feature request",
        priority: "Low",
        category: "General Inquiry",
        status: "New",
        lastReply: null,
        createdDate: new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000),
        lastUpdated: new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000),
        businessId: CURRENT_BUSINESS_ID,
      },
    ];
    return raw.map((t, i) => {
      const u = TICKET_USERS[i % TICKET_USERS.length];
      return { ...t, userDisplayName: u.name, userAvatar: u.image };
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 400));
      const all = generateMyTickets();
      const byStatus =
        statusFilter == null
          ? all
          : statusFilter === "Open"
            ? all.filter((t) => t.status === "Open" || t.status === "New")
            : all.filter((t) => t.status === statusFilter);
      const filtered = [...byStatus].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (sortField === "createdDate" || sortField === "lastUpdated") {
          aVal = aVal?.getTime?.() ?? 0;
          bVal = bVal?.getTime?.() ?? 0;
        }
        if (aVal === null || aVal === undefined) return sortOrder === "asc" ? 1 : -1;
        if (bVal === null || bVal === undefined) return sortOrder === "asc" ? -1 : 1;
        if (typeof aVal === "string") return sortOrder === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      });
      setTotalPages(Math.max(1, Math.ceil(filtered.length / perPage)));
      const start = (currentPage - 1) * perPage;
      setTickets(filtered.slice(start, start + perPage));
      setLoading(false);
    };
    run();
  }, [currentPage, sortField, sortOrder, statusFilter]);

  const formatDate = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatTimeAgo = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(d);
  };

  return (
    <DashboardMainContainer className="!bg-white">
      <div className={BUSINESS_INBOX_CONTENT_CLASS}>
        <div className={`${BUSINESS_TABLE_CLASS} overflow-hidden`}>
          {loading ? (
            <div className="divide-y divide-lightgray">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center w-full py-5 px-2">
                  <div className="h-12 w-12 rounded-full bg-lightgray animate-pulse flex-shrink-0" />
                  <div className="flex-1 min-w-0 ml-4 space-y-2">
                    <div className="h-4 w-48 bg-lightgray animate-pulse rounded" />
                    <div className="h-3 w-64 bg-lightgray animate-pulse rounded" />
                  </div>
                  <div className="h-4 w-16 bg-lightgray animate-pulse rounded ml-4" />
                </div>
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="py-12 text-center">
              <AdminSupportingText className="text-mediumgray block">No tickets yet.</AdminSupportingText>
              <AdminHeaderAction
                className="mt-4"
                onClick={() => navigate("/business-dashboard/tickets/create")}
              >
                Create ticket
              </AdminHeaderAction>
            </div>
          ) : (
            <>
              <div className="divide-y divide-lightgray">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticketId}
                    className="flex items-center w-full py-5 px-2 hover:bg-mistygray transition-colors duration-200 cursor-pointer"
                    onClick={() =>
                      navigate(`/business-dashboard/tickets/${ticket.ticketId}`, {
                        state: { ticket },
                      })
                    }
                  >
                    <div className="flex items-center gap-4 flex-shrink-0" style={{ width: "280px", minWidth: "280px" }}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <img
                          src={ticket.userAvatar || DEFAULT_AVATAR}
                          alt={ticket.userDisplayName || ""}
                          className="size-12 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_AVATAR;
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <AdminTableBody className="block truncate text-offblack">
                            {ticket.userDisplayName || "—"}
                          </AdminTableBody>
                        </div>
                      </div>
                    </div>
                    <div className="flex-2 min-w-0 mx-4 text-left" style={{ maxWidth: "50%" }}>
                      <div className="truncate text-left">
                        <AdminTableBody emphasis className="text-offblack block">
                          {ticket.category}
                        </AdminTableBody>
                        <AdminTableBody className="text-offblack truncate block">
                          {ticket.lastReply || "—"}
                        </AdminTableBody>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 text-right">
                      <AdminMetadataText className="text-offblack">
                        {formatTimeAgo(ticket.lastUpdated) || formatDate(ticket.lastUpdated)}
                      </AdminMetadataText>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <AdminDetailPaginationFooter
                  className="border-t border-lightgray"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  goToPage={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default AllTickets;
